import React from 'react';

/**
 * KD-3581 §7 — the program slot of a day that carries no experience.
 *
 * Shown after an experience is removed and on a day never composed: the day
 * skeleton stays on screen rather than collapsing, so the timeline keeps its
 * length. The card is inert on purpose — picking an experience back is done
 * from the day's own selector, not from here.
 *
 * Two looks, for two different meanings: `dashed` for a slot still waiting to
 * be filled, `filled` for a slot that is settled and simply holds no product —
 * a day with no accommodation, an accommodation booked outside KOOB. The grey
 * card matches what the editable panel shows for those same states, so a day
 * does not change appearance between the live composer and a read version.
 */
type EmptyProgramCardProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
> & {
  label: React.ReactNode;
  variant?: 'dashed' | 'filled';
};

const variantClassNames = {
  dashed: 'border-dashed border-gray-300 bg-white/40',
  filled: 'border-solid border-gray-300 bg-gray-200',
};

const EmptyProgramCard = React.forwardRef<
  HTMLDivElement,
  EmptyProgramCardProps
>(({ label, variant = 'dashed', ...props }, ref) => (
  <div
    ref={ref}
    className={`flex items-center justify-center rounded-md border px-3 py-6 text-center text-sm text-gray-500 ${variantClassNames[variant]}`}
    {...props}
  >
    {label}
  </div>
));

EmptyProgramCard.displayName = 'EmptyProgramCard';

export default EmptyProgramCard;
